import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { GrowerFormSubmitResult } from './add-grower-section/use-grower-form';

export type SingleGrowerFormApi = {
    isValid: boolean;
    isSubmitting: boolean;
    submit: () => Promise<GrowerFormSubmitResult>;
};

type SingleGrowerFormContextValue = {
    api: SingleGrowerFormApi;
    register: (
        registration: Omit<SingleGrowerFormApi, 'submit'> & { submit: () => Promise<GrowerFormSubmitResult> }
    ) => void;
    unregister: () => void;
};

const defaultMeta = { isValid: false, isSubmitting: false };

const SingleGrowerFormContext = createContext<SingleGrowerFormContextValue | null>(null);

export function SingleGrowerFormProvider({ children }: { children: ReactNode }) {
    const submitRef = useRef<(() => Promise<GrowerFormSubmitResult>) | null>(null);
    const [meta, setMeta] = useState(defaultMeta);

    const register = useCallback(
        (registration: { isValid: boolean; isSubmitting: boolean; submit: () => Promise<GrowerFormSubmitResult> }) => {
            submitRef.current = registration.submit;
            setMeta((prev) =>
                prev.isValid === registration.isValid && prev.isSubmitting === registration.isSubmitting
                    ? prev
                    : { isValid: registration.isValid, isSubmitting: registration.isSubmitting }
            );
        },
        []
    );

    const unregister = useCallback(() => {
        submitRef.current = null;
        setMeta(defaultMeta);
    }, []);

    const api = useMemo<SingleGrowerFormApi>(
        () => ({
            ...meta,
            submit: () => submitRef.current?.() ?? Promise.resolve({ success: false }),
        }),
        [meta]
    );

    const value = useMemo(() => ({ api, register, unregister }), [api, register, unregister]);

    return <SingleGrowerFormContext.Provider value={value}>{children}</SingleGrowerFormContext.Provider>;
}

export function useSingleGrowerFormApi() {
    return useContext(SingleGrowerFormContext)?.api ?? null;
}

export function useRegisterSingleGrowerFormApi(
    registration: { isValid: boolean; isSubmitting: boolean; submit: () => Promise<GrowerFormSubmitResult> } | null
) {
    const register = useContext(SingleGrowerFormContext)?.register;
    const unregister = useContext(SingleGrowerFormContext)?.unregister;

    useEffect(() => {
        if (!register || !unregister) return;
        if (registration) {
            register(registration);
            return;
        }
        unregister();
    }, [register, unregister, registration?.isValid, registration?.isSubmitting, registration?.submit]);

    useEffect(() => unregister, [unregister]);
}
